/**
 * Shared code between client and server
 * Useful to share types between client and server
 * and/or small pure JS functions that can be used on both client and server
 */

/**
 * Example response type for /api/demo
 */
export interface DemoResponse {
  message: string;
}

export interface NumerologyAnalyzeRequest {
  package?: "pin" | "isim" | "normal" | "premium";
  firstName: string;
  lastName: string;
  day?: number;
  month?: number;
  year?: number;
}

export interface NumerologyAnalyzeResponse {
  package: "pin" | "isim" | "normal" | "premium";
  person: {
    firstName: string;
    lastName: string;
    fullName: string;
    birthDate?: {
      day: number;
      month: number;
      year: number;
    };
  };
  calculations: {
    hasBirthDate: boolean;
    destinyPath?: string;
    destinyReduced?: number;
    color?: string;
    element?: string;
    birthDayNumber?: number;
    expressionNumber: number;
    heartDesireNumber: number;
    personalityNumber: number;
    karmicDebtNumbers: number[];
    pinCodes?: number[];
    yearForesight?: string;
  };
  nameEquivalents: Array<{ char: string; value: number }>;
  context: Record<string, string>;
}

export interface NumerologyRenderRequest {
  template: string;
  context: Record<string, unknown>;
}

export interface NumerologyRenderResponse {
  rendered: string;
}

export type MembershipPlanCode = "free" | "premium";
export type MembershipBillingPeriod = "weekly" | "monthly" | "semi_annual" | "annual";

export type MembershipFeature = {
  key: string;
  label: string;
  includedInFree: boolean;
  includedInPlus: boolean;
  includedInPro: boolean;
};

export type MembershipPlan = {
  code: MembershipPlanCode;
  name: string;
  tagline: string;
  badge?: string;
  pricesTry: Partial<Record<MembershipBillingPeriod, number>>;
  highlightedFeatures: string[];
};

export const MEMBERSHIP_FEATURES: MembershipFeature[] = [
  {
    key: "daily_discovery",
    label: "Günlük 10 keşif hakkı",
    includedInFree: true,
    includedInPlus: true,
    includedInPro: true,
  },
  {
    key: "unlimited_discovery",
    label: "Sınırsız keşfet / beğeni",
    includedInFree: false,
    includedInPlus: true,
    includedInPro: true,
  },
  {
    key: "chat_limit",
    label: "Aynı anda 3 aktif sohbet limiti",
    includedInFree: true,
    includedInPlus: false,
    includedInPro: false,
  },
  {
    key: "likes_you",
    label: "Beni beğenenleri gör",
    includedInFree: false,
    includedInPlus: true,
    includedInPro: true,
  },
  {
    key: "advanced_filters",
    label: "Gelişmiş filtreler",
    includedInFree: false,
    includedInPlus: true,
    includedInPro: true,
  },
  {
    key: "full_wellness_library",
    label: "Tüm meditasyon/nefes arşivi",
    includedInFree: false,
    includedInPlus: true,
    includedInPro: true,
  },
  {
    key: "priority_visibility",
    label: "Keşfette öncelikli görünürlük",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
  {
    key: "priority_support",
    label: "Öncelikli destek",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
  {
    key: "detailed_compatibility",
    label: "Detaylı uyum analizleri",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
  {
    key: "vip_badge",
    label: "VIP rozet",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
  {
    key: "nearby_rituals",
    label: "Yakınımdaki ritüeller erişimi",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
  {
    key: "exclusive_circles",
    label: "Özel çember / etkinlik erişimi",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
  {
    key: "direct_message_without_mutual_daily_5",
    label: "Karşılıklı beğeni olmadan günlük 5 kişiye ilk mesaj",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
  {
    key: "member_store_discount",
    label: "Üyelere özel mağaza indirimi",
    includedInFree: false,
    includedInPlus: true,
    includedInPro: true,
  },
  {
    key: "detailed_numerology",
    label: "Detaylı Numeroloji Analizi",
    includedInFree: false,
    includedInPlus: true,
    includedInPro: true,
  },
  {
    key: "numerology_report_pdf",
    label: "Numeroloji PDF Rapor İndirme",
    includedInFree: false,
    includedInPlus: false,
    includedInPro: true,
  },
];

export const MEMBERSHIP_PLANS: MembershipPlan[] = [
  {
    code: "free",
    name: "Ücretsiz",
    tagline: "Temel kullanım",
    badge: undefined,
    pricesTry: {},
    highlightedFeatures: [
      "Günlük keşif",
      "Temel meditasyon",
      "Çemberlere katılım",
      "Eşleşince mesajlaşma",
    ],
  },
  {
    code: "premium",
    name: "Premium",
    tagline: "Tüm özellikler açık",
    badge: "Önerilen",
    pricesTry: {
      monthly: 199,
      annual: 1499,
    },
    highlightedFeatures: [
      "Sınırsız keşif",
      "Detaylı Numeroloji Analizi",
      "Beni beğenenleri gör",
      "Tüm meditasyon arşivi",
      "VIP rozet",
      "Öncelikli görünürlük",
    ],
  },
];
